import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { rpc } from '@/lib/rpc';
import { downloadTemplate, parseLeadSheet } from './importSheet';
import { leadKeys } from './useLeads';

interface ImportRow {
  row: number;
  status: 'created' | 'will_create' | 'duplicate' | 'error';
  phone?: string;
  name?: string;
  existing_name?: string;
  message?: string;
}

interface ImportResult {
  dry_run: boolean;
  total: number;
  created: number;
  duplicates: number;
  errors: number;
  rows: ImportRow[];
}

export function ImportLeadsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [parsed, setParsed] = useState<Record<string, string>[] | null>(null);
  const [unmapped, setUnmapped] = useState<string[]>([]);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [done, setDone] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const runImport = useMutation({
    mutationFn: ({ rows, dryRun }: { rows: Record<string, string>[]; dryRun: boolean }) =>
      rpc<ImportResult>('import_leads', { p_rows: rows, p_dry_run: dryRun }),
  });

  const reset = () => {
    setParsed(null);
    setUnmapped([]);
    setPreview(null);
    setDone(null);
    setError(null);
    setFileName(null);
  };

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    reset();
    setFileName(file.name);
    setParsing(true);
    try {
      const sheet = await parseLeadSheet(file);
      if (sheet.rows.length === 0) {
        setError(t('leads.import.empty'));
        return;
      }
      if (!sheet.matchedFields.includes('full_name') || !sheet.matchedFields.includes('phone')) {
        setError(t('leads.import.missingColumns'));
        setUnmapped(sheet.unmappedHeaders);
        return;
      }

      setParsed(sheet.rows);
      setUnmapped(sheet.unmappedHeaders);

      // Always dry-run first. Nothing is written until the person has seen what
      // would happen — an import that silently half-worked is worse than one that
      // refused, because nobody knows which half.
      const result = await runImport.mutateAsync({ rows: sheet.rows, dryRun: true });
      setPreview(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    } finally {
      setParsing(false);
    }
  };

  const confirm = async () => {
    if (!parsed) return;
    setError(null);
    try {
      const result = await runImport.mutateAsync({ rows: parsed, dryRun: false });
      setDone(result);
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  const close = () => {
    reset();
    onClose();
  };

  const result = done ?? preview;
  const problems = result?.rows.filter((r) => r.status === 'error') ?? [];
  const duplicates = result?.rows.filter((r) => r.status === 'duplicate') ?? [];

  return (
    <Modal open={open} title={t('leads.import.title')} onClose={close}>
      <div className="flex flex-col gap-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        {!done ? (
          <>
            <p className="text-sm text-slate-600">{t('leads.import.intro')}</p>

            <button
              type="button"
              onClick={() => void downloadTemplate()}
              className="inline-flex w-fit items-center gap-2 text-sm font-medium text-brand-700 hover:underline"
            >
              <Download className="size-4" aria-hidden="true" />
              {t('leads.import.template')}
            </button>

            {/* The native file input renders "Choose File / No file chosen" in the
                browser's own language, which is English on most machines here and
                cannot be translated. Hiding it behind a label gives us a control we
                can actually localise, and shows the chosen filename ourselves. */}
            <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800">
              <Upload className="size-4" aria-hidden="true" />
              {t('leads.import.choose')}
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => void onFile(e)}
                className="sr-only"
              />
            </label>

            {fileName ? <p className="text-sm text-slate-600">{fileName}</p> : null}
          </>
        ) : null}

        {parsing ? <Spinner /> : null}

        {unmapped.length > 0 ? (
          <Alert tone="info">{t('leads.import.unmapped', { headers: unmapped.join('، ') })}</Alert>
        ) : null}

        {result ? (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-4 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
              <div>
                <div className="text-lg font-semibold text-slate-900">{result.total}</div>
                <div className="text-xs text-slate-500">{t('leads.import.rows')}</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-green-700">{result.created}</div>
                <div className="text-xs text-slate-500">
                  {done ? t('leads.import.created') : t('leads.import.willCreate')}
                </div>
              </div>
              <div>
                <div className="text-lg font-semibold text-amber-700">{result.duplicates}</div>
                <div className="text-xs text-slate-500">{t('leads.import.duplicates')}</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-red-700">{result.errors}</div>
                <div className="text-xs text-slate-500">{t('leads.import.errors')}</div>
              </div>
            </div>

            {duplicates.length > 0 ? (
              <Alert tone="info">{t('leads.import.duplicatesNote')}</Alert>
            ) : null}

            {problems.length > 0 ? (
              <div className="max-h-48 overflow-y-auto rounded-xl border border-red-200">
                <table className="w-full text-xs">
                  <thead className="bg-red-50 text-red-900">
                    <tr>
                      <th className="px-2 py-1 text-start">{t('leads.import.row')}</th>
                      <th className="px-2 py-1 text-start">{t('leads.fullName')}</th>
                      <th className="px-2 py-1 text-start">{t('leads.import.problem')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {problems.map((p) => (
                      <tr key={p.row}>
                        <td className="px-2 py-1">{p.row}</td>
                        <td className="px-2 py-1">{p.name || '—'}</td>
                        <td className="px-2 py-1 text-red-700">{p.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            {done ? t('common.cancel') : t('common.cancel')}
          </Button>
          {preview && !done ? (
            <Button
              loading={runImport.isPending}
              disabled={preview.created === 0}
              onClick={() => void confirm()}
            >
              {t('leads.import.confirm', { count: preview.created })}
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
